import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Analytics: React.FC = () => {
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('7d');

  // Sample data - replace with actual API data
  const getDataForRange = (range: string) => {
    switch (range) {
      case '7d':
        return {
          days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
          productImagesData: [12, 19, 15, 25, 22, 30, 18],
          tryOnData: [8, 12, 10, 15, 18, 20, 14],
          fashionExtractorData: [5, 8, 6, 12, 9, 15, 7],
        };
      case '30d':
        return {
          days: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
          productImagesData: [85, 92, 78, 95],
          tryOnData: [65, 70, 58, 72],
          fashionExtractorData: [35, 42, 38, 45],
        };
      case '90d':
        return {
          days: ['Month 1', 'Month 2', 'Month 3'],
          productImagesData: [350, 380, 420],
          tryOnData: [280, 310, 340],
          fashionExtractorData: [120, 150, 180],
        };
      default:
        return {
          days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
          productImagesData: [12, 19, 15, 25, 22, 30, 18],
          tryOnData: [8, 12, 10, 15, 18, 20, 14],
          fashionExtractorData: [5, 8, 6, 12, 9, 15, 7],
        };
    }
  };

  const { days, productImagesData, tryOnData, fashionExtractorData } = getDataForRange(dateRange);

  const data = {
    labels: days,
    datasets: [
      {
        label: 'Product Images Generated',
        data: productImagesData,
        backgroundColor: 'rgba(79, 70, 229, 0.5)',
        borderColor: 'rgb(79, 70, 229)',
        borderWidth: 1,
      },
      {
        label: 'Try-On Sessions',
        data: tryOnData,
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 1,
      },
      {
        label: 'Fashion Extractions',
        data: fashionExtractorData,
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        borderColor: 'rgb(34, 197, 94)',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Activity Overview',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 5,
        },
      },
    },
  };

  // Calculate totals
  const totalProductImages = productImagesData.reduce((a, b) => a + b, 0);
  const totalTryOns = tryOnData.reduce((a, b) => a + b, 0);
  const totalFashionExtractions = fashionExtractorData.reduce((a, b) => a + b, 0);
  const averageProductImages = Math.round(totalProductImages / days.length);
  const averageTryOns = Math.round(totalTryOns / days.length);
  const averageFashionExtractions = Math.round(totalFashionExtractions / days.length);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
        <div className="flex space-x-2 bg-white p-1 rounded-lg shadow-sm">
          <button
            onClick={() => setDateRange('7d')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              dateRange === '7d'
                ? 'bg-secondary-500 text-white'
                : 'text-text-description hover:bg-accent-sand'
            }`}
          >
            7 Days
          </button>
          <button
            onClick={() => setDateRange('30d')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              dateRange === '30d'
                ? 'bg-secondary-500 text-white'
                : 'text-text-description hover:bg-accent-sand'
            }`}
          >
            30 Days
          </button>
          <button
            onClick={() => setDateRange('90d')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              dateRange === '90d'
                ? 'bg-secondary-500 text-white'
                : 'text-text-description hover:bg-accent-sand'
            }`}
          >
            90 Days
          </button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-text-description">Total Product Images</h3>
          <p className="text-3xl font-bold text-secondary-600">{totalProductImages}</p>
          <p className="text-sm text-text-description">Average: {averageProductImages} per {dateRange === '7d' ? 'day' : dateRange === '30d' ? 'week' : 'month'}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-text-description">Total Try-On Sessions</h3>
          <p className="text-3xl font-bold text-green-600">{totalTryOns}</p>
          <p className="text-sm text-text-description">Average: {averageTryOns} per {dateRange === '7d' ? 'day' : dateRange === '30d' ? 'week' : 'month'}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-text-description">Fashion Extractions</h3>
          <p className="text-3xl font-bold text-emerald-600">{totalFashionExtractions}</p>
          <p className="text-sm text-text-description">Average: {averageFashionExtractions} per {dateRange === '7d' ? 'day' : dateRange === '30d' ? 'week' : 'month'}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-text-description">Peak Usage {dateRange === '7d' ? 'Day' : dateRange === '30d' ? 'Week' : 'Month'}</h3>
          <p className="text-3xl font-bold text-purple-600">
            {days[productImagesData.indexOf(Math.max(...productImagesData))]}
          </p>
          <p className="text-sm text-text-description">Most active period</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Daily Activity</h2>
          <Bar data={data} options={options} />
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Usage Trends</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-secondary-600">Product Images</span>
                <span className="text-sm font-medium text-text-description">{totalProductImages} total</span>
              </div>
              <div className="w-full bg-accent-taupe rounded-full h-2.5">
                <div
                  className="bg-secondary-500 h-2.5 rounded-full"
                  style={{ width: `${(totalProductImages / (dateRange === '7d' ? 150 : dateRange === '30d' ? 600 : 1800)) * 100}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-green-600">Try-On Sessions</span>
                <span className="text-sm font-medium text-text-description">{totalTryOns} total</span>
              </div>
              <div className="w-full bg-accent-taupe rounded-full h-2.5">
                <div
                  className="bg-green-600 h-2.5 rounded-full"
                  style={{ width: `${(totalTryOns / (dateRange === '7d' ? 100 : dateRange === '30d' ? 400 : 1200)) * 100}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-emerald-600">Fashion Extractions</span>
                <span className="text-sm font-medium text-text-description">{totalFashionExtractions} total</span>
              </div>
              <div className="w-full bg-accent-taupe rounded-full h-2.5">
                <div
                  className="bg-emerald-600 h-2.5 rounded-full"
                  style={{ width: `${(totalFashionExtractions / (dateRange === '7d' ? 80 : dateRange === '30d' ? 320 : 960)) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 